import { authFetch } from './http';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL
  ? `${process.env.REACT_APP_API_BASE_URL}/api/v1`
  : '/efx-csp/api/v1';

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  dateTime?: string;
  data?: T;
  status?: number;
}

export interface InvalidNumber {
  lineNumber: number;
  value: string;
  reason: string;
}

export interface BulkSmsSendResult {
  accepted: boolean;
  responseCode: number;
  responseDesc: string;
  processAt?: string;
  totalRows: number;
  sentCount: number;
  duplicateCount: number;
  invalidCount: number;
  sentNumbers: string[];
  invalidNumbers: InvalidNumber[];
}

export interface SmsMessage {
  id: number;
  contact: string;
  messageId: string;
  statusCode: number;
  statusDescription: string;
  deliveryStatus: string | null;
  deliveryTime: string | null;
  deliveryNetworkCode: number;
  deliveryNetwork: string;
  batchId: number;
  batchType: string;
  batchPurpose: string;
  batchStatus: string;
  message: string;
  createdOn: string;
}

export interface SmsMessagesPage {
  items: SmsMessage[];
  hasNext: boolean;
  nextCursor?: string;
  size: number;
}

export interface SmsMessagesSearchParams {
  cursor?: string;
  limit?: number;
  contactNumber?: string;
  deliveryTimeFrom?: string;
  deliveryTimeTo?: string;
  deliveryStatus?: string;
  deliveryNetwork?: string;
  batchStatus?: string;
  purpose?: string;
  batchType?: string;
}

async function parseResponse<T>(res: Response): Promise<ApiResponse<T>> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return {
      success: false,
      status: res.status,
      message: data?.message ?? `Request failed: ${res.status} ${res.statusText}`,
    };
  }
  return { ...(data as ApiResponse<T>), status: res.status };
}

function toErrorResponse<T>(error: unknown): ApiResponse<T> {
  const message = error instanceof Error ? error.message : 'A network error occurred.';
  return { success: false, message };
}

function buildQuery(params: Record<string, string | undefined>): string {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value?.trim()) query.set(key, value.trim());
  });
  return query.toString();
}

export async function sendBulkSms(
  file: File,
  message: string,
  processAt?: string,
): Promise<ApiResponse<BulkSmsSendResult>> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('message', message);
  // Kenya time (EAT) as yyyy-MM-ddTHH:mm with no Z or offset; omit to send immediately.
  if (processAt) formData.append('processAt', processAt);

  try {
    const res = await authFetch(`${apiBaseUrl}/bulk-sms/send`, {
      method: 'POST',
      headers: { Accept: '*/*' },
      body: formData,
    });
    return parseResponse<BulkSmsSendResult>(res);
  } catch (error) {
    return toErrorResponse<BulkSmsSendResult>(error);
  }
}

export async function searchSmsMessages(
  params: SmsMessagesSearchParams = {},
): Promise<ApiResponse<SmsMessagesPage>> {
  const query = buildQuery({
    cursor: params.cursor,
    limit: params.limit !== undefined ? String(params.limit) : undefined,
    contactNumber: params.contactNumber,
    deliveryTimeFrom: params.deliveryTimeFrom,
    deliveryTimeTo: params.deliveryTimeTo,
    deliveryStatus: params.deliveryStatus,
    deliveryNetwork: params.deliveryNetwork,
    batchStatus: params.batchStatus,
    purpose: params.purpose,
    batchType: params.batchType,
  });
  const url = `${apiBaseUrl}/bulk-sms/messages${query ? `?${query}` : ''}`;

  try {
    const res = await authFetch(url, { method: 'GET', headers: { Accept: '*/*' } });
    return parseResponse<SmsMessagesPage>(res);
  } catch (error) {
    return toErrorResponse<SmsMessagesPage>(error);
  }
}
